// Меню на холсте (README «Каркас экрана»; contracts/ui.md, «Каркас»): личное без заголовка —
// «Главная» и рабочие разделы; группа служебных экранов «Сопровождение» (002) — у сопровождения.
// Внизу — пункт «Настройки» над карточкой пользователя (007; ux.md «Табеля», раздел 11):
// администрирование живёт в «Настройках», а не группой меню; там же с 0.22.2 «Состояние»
// и «Выкатки» (группы «Сервис» в меню нет). Между разделами и «Настройками» —
// слот блока страницы (дерево протокола, 012). Под карточкой — версия сборки. Клавиши перехода (G H, G U, G J, G T, G S) — в подсказках
// пунктов (ux.md, раздел 8); у свёрнутого меню подсказка называет и сам пункт, а у карточки
// пользователя — ФИО и роль (README «Каркас экрана»). Счётчики — только то, что требует действия,
// ноль не показывается (navCounters): у «Оповещения» (002) — число действующих, с 032 серый;
// у «Справочника» (005, макет 5) — «К разбору», серый. Пункт подсвечен и на экранах своего раздела
// с другим адресом: открытый протокол (/protocols/…) — «Документы» (navActiveElsewhere).
//
// 015 (FR-021; ux CHK006): «Выйти» при неотправленных правках таблиц протокола этого пользователя —
// окно «Выйти и потерять правки?» с их числом (useConfirmedLogout; им же пользуется палитра).
//
// 022 (T031; spec, сценарий 3.2; COLLAB-02): у внешнего разделов «Документы», «Контроль»,
// «Справочник» нет по правам, а «Настройки» скрыты по признаку me.external: настройки установки
// ему закрыты, свой профиль — в меню пользователя.
//
// 030 (T019, T020; FR-031, FR-033; ASG-DESK-08, ASG-DESK-15, ASG-ONB-14): «Мои задачи» — после
// «Главной» у связанного с человеком, счётчик «просрочено и на сегодня» серый. Сеанс по ссылке —
// узкое меню: «Мои задачи», «Помощь» (окно вопроса контролёру), карточка «Вы вошли как Новиков
// Н. Н.» с «Выйти»; «Настроек», «Профиля» и «Списка клавиш» нет — сеанс даёт только свои пункты.
//
// 032 (T026; FR-002, FR-003; ASG-HIST-03, ASG-HIST-04): «Входящие» — после «Главной» (у кого она —
// «Мои задачи», после них), и в узком меню сеанса по ссылке. Счётчик — непрочитанные «требует
// действия» из того же опроса, что у колокольчика; он единственный акцентный, «Оповещения» серые.
import type { MeResponse } from '@kp/contracts';
import { Avatar, Dropdown, DropdownItem, DropdownSeparator, Icon, Tooltip, cx } from '@kp/ui';
import { Keyboard, LifeBuoy, LogOut, Settings, UserRound } from '@kp/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import type { LinkProps } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { apiQuery } from '../api/client.ts';
import { rolesText } from '../app/labels.ts';
import { LogoutDialog } from '../features/minutes/status/LeaveDialogs.tsx';
import { BuildVersion } from './BuildVersion.tsx';
import { useShell } from './context.tsx';
import { goShortcutFor } from './keys.ts';
import { mySummaryQuery } from './my-summary.ts';
import { useInboxCounts } from './NotificationsFeed.tsx';
import {
  can,
  navActiveElsewhere,
  navCounters,
  readsDirectory,
  sections,
  SUPPORT_GROUP_TITLE,
  visibleNavItems,
} from './permissions.ts';
import type { NavCounter, NavCounterSource, NavItem } from './permissions.ts';
import { useLogout, useMe, useUnsentEdits } from './session.ts';
import { settingsItemFor } from './settings-sections.ts';
import { triageQuery } from './triage.ts';

/**
 * Число действующих оповещений для счётчика в меню: фоновый опрос краткой панели раз в минуту
 * с X-KP-Background — сеанс он не продлевает (FR-008б 001). Опрос идёт, только если пункт
 * со счётчиком виден.
 */
export function activeAlertsQuery() {
  return {
    ...apiQuery('ops.health', { background: true }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  };
}

/** Счётчики пунктов меню по адресу пункта: только ненулевые. */
function useNavCounts(me: MeResponse, items: readonly NavItem[]): ReadonlyMap<string, number> {
  const { permissions } = me;
  const shown = (source: NavCounterSource) =>
    items.some((item) => navCounters[item.to]?.source === source);
  const alertsOn = can(permissions, ['ops.alerts.read']) && shown('activeAlerts');
  // «К разбору» — тем, кто читает справочник без временного доступа: иначе сервер ответит ACC-003.
  const triageOn = readsDirectory(permissions) && shown('triage');
  // Свои пункты — только у связанного с человеком: без связи сервер ответит ACC-008.
  const mineOn = me.person !== null && shown('mine');
  // 032: тот же фоновый опрос, что у колокольчика, — второго запроса нет.
  const inboxOn = shown('inbox');
  const inbox = useInboxCounts(inboxOn).action;
  const active =
    useQuery({ ...activeAlertsQuery(), enabled: alertsOn, select: (data) => data.activeAlerts })
      .data ?? 0;
  const triage =
    useQuery({ ...triageQuery(), enabled: triageOn, select: (data) => data.total }).data ?? 0;
  const mine =
    useQuery({
      ...mySummaryQuery(),
      enabled: mineOn,
      select: (data) => data.overdue + data.today,
    }).data ?? 0;
  const values: Record<NavCounterSource, number> = {
    activeAlerts: alertsOn ? active : 0,
    triage: triageOn ? triage : 0,
    mine: mineOn ? mine : 0,
    inbox: inboxOn ? inbox : 0,
  };
  const counts = new Map<string, number>();
  for (const item of items) {
    const counter = navCounters[item.to];
    const value = counter ? values[counter.source] : 0;
    if (value > 0) counts.set(item.to, value);
  }
  return counts;
}

interface SidebarContentProps {
  /** Свёрнутое меню: колонка иконок, названия — в подсказке справа. */
  collapsed?: boolean;
  /** Переход закрывает меню поверх экрана (до 900 px). */
  onNavigate?: () => void;
  /** Версия сборки внизу меню; до 900 px она в углу листа, а не в меню поверх экрана. */
  showBuild?: boolean;
  /**
   * Подсказки с клавишами у пунктов. В меню поверх экрана (до 900 px) их нет: окно ставит фокус
   * на первый пункт, и подсказка всплывала бы при каждом открытии; клавиши — в палитре и в `?`.
   */
  keyHints?: boolean;
}

export function SidebarContent({
  collapsed = false,
  onNavigate,
  showBuild = true,
  keyHints = true,
}: SidebarContentProps) {
  const me = useMe();
  const items = visibleNavItems(me.permissions, me);
  const personal = items.filter((item) => item.group === 'personal');
  const support = items.filter((item) => item.group === 'support');
  const counts = useNavCounts(me, items);
  const jobsOnJ = can(me.permissions, sections.jobs.operations);
  const { setMenuSlot } = useShell();
  const navRef = useRef<HTMLElement>(null);
  // Меню поверх экрана (до 900 px) ставит фокус на текущий раздел или первый пункт. Без этого
  // окно взяло бы первую кнопку — кнопку блока страницы (дерево протокола) с её подсказкой.
  const overlay = onNavigate !== undefined;
  useEffect(() => {
    if (!overlay) return;
    const nav = navRef.current;
    const link =
      nav?.querySelector<HTMLElement>('a[aria-current="page"]') ??
      nav?.querySelector<HTMLElement>('a');
    link?.focus();
  }, [overlay]);

  return (
    <div className={cx('kp-sidebar-in', collapsed && 'is-collapsed')}>
      <div className="kp-brand">
        <span className="kp-brand-mark" aria-hidden="true">
          КП
        </span>
        <span className="kp-brand-name">Контроль протоколов</span>
      </div>
      <nav ref={navRef} aria-label="Разделы" className="kp-nav">
        <ul>
          {personal.map((item) => (
            <NavLink
              key={item.id}
              item={item}
              collapsed={collapsed}
              keyHints={keyHints}
              jobsOnJ={jobsOnJ}
              count={counts.get(item.to)}
              onNavigate={onNavigate}
            />
          ))}
          {me.linkSession ? <HelpNavItem collapsed={collapsed} onNavigate={onNavigate} /> : null}
        </ul>
        {support.length > 0 ? (
          <>
            <h2 className="kp-nav-group" id="kp-nav-support">
              {SUPPORT_GROUP_TITLE}
            </h2>
            <ul aria-labelledby="kp-nav-support">
              {support.map((item) => (
                <NavLink
                  key={item.id}
                  item={item}
                  collapsed={collapsed}
                  keyHints={keyHints}
                  jobsOnJ={jobsOnJ}
                  count={counts.get(item.to)}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          </>
        ) : null}
        {/* Блок страницы под разделами (012: дерево «Структура» протокола); у свёрнутого меню его
            нет, как в прототипе. */}
        {collapsed ? null : <div ref={setMenuSlot} className="kp-menu-slot" />}
        {me.external || me.linkSession ? null : (
          <ul className="kp-nav-bottom">
            <SettingsLink collapsed={collapsed} onNavigate={onNavigate} />
          </ul>
        )}
      </nav>
      <div className="kp-sidebar-foot">
        <UserMenu collapsed={collapsed} onNavigate={onNavigate} />
        {showBuild ? <BuildVersion compact={collapsed} /> : null}
      </div>
    </div>
  );
}

function NavLink({
  item,
  collapsed,
  keyHints,
  jobsOnJ,
  count,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  keyHints: boolean;
  /** G J ведёт в «Фоновые задачи» (keys.ts). */
  jobsOnJ: boolean;
  /** Счётчик пункта; нет или ноль — не показывается. */
  count?: number;
  onNavigate?: () => void;
}) {
  const counter: NavCounter | undefined = navCounters[item.to];
  // Открытый протокол — экран «Документов»: пункт подсвечен и там (адрес у протокола свой).
  const elsewhere = useRouterState({
    select: (state) => navActiveElsewhere(item.to, state.location.pathname),
  });
  const link = (
    <Link
      to={item.to as LinkProps['to']}
      activeOptions={{ exact: item.to === '/' }}
      activeProps={{ className: 'is-active', 'aria-current': 'page' }}
      className={cx('kp-nav-item', elsewhere && 'is-active')}
      {...(elsewhere ? { 'aria-current': 'page' as const } : {})}
      onClick={onNavigate}
      {...(count && counter ? { 'aria-label': `${item.label}, ${counter.spoken}: ${count}` } : {})}
    >
      <Icon icon={item.icon} />
      <span className="kp-nav-label">{item.label}</span>
      {count ? (
        <span
          className={cx('kp-nav-count t-num', counter?.accent && 'is-accent')}
          aria-hidden="true"
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </Link>
  );
  const shortcut = keyHints ? goShortcutFor(item.to, jobsOnJ) : undefined;
  return (
    <li>
      {collapsed || shortcut ? (
        <Tooltip content={item.label} shortcut={shortcut} side="right">
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}

/**
 * «Помощь» в меню сеанса по ссылке (030: FR-031, FR-032): не раздел, а окно вопроса контролёру —
 * кнопка в виде пункта меню. В меню поверх экрана (до 900 px) нажатие сначала закрывает меню.
 */
function HelpNavItem({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { openHelp } = useShell();
  const button = (
    <button
      type="button"
      className="kp-nav-item"
      onClick={() => {
        onNavigate?.();
        openHelp();
      }}
    >
      <Icon icon={LifeBuoy} />
      <span className="kp-nav-label">Помощь</span>
    </button>
  );
  return (
    <li>
      {collapsed ? (
        <Tooltip content="Помощь" side="right">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </li>
  );
}

/**
 * «Настройки» внизу меню, над карточкой пользователя (ux.md «Табеля», раздел 11). Пункт активен
 * на любом разделе настроек: и на /settings/…, и на /users, /audit, /profile.
 */
function SettingsLink({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const active = settingsItemFor(pathname) !== undefined;
  const link = (
    <Link
      to="/settings"
      className={cx('kp-nav-item', active && 'is-active')}
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
    >
      <Icon icon={Settings} />
      <span className="kp-nav-label">Настройки</span>
    </Link>
  );
  return (
    <li>
      {collapsed ? (
        <Tooltip content="Настройки" side="right">
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}

/**
 * «Выйти» с проверкой неотправленных правок (015: FR-021; ux CHK006): правки таблиц протокола этого
 * пользователя ещё в браузере — окно «Выйти и потерять правки?» с их числом, после подтверждения
 * очередь пользователя очищается (useLogout); правок нет — выход сразу. dialog — окно, его
 * рисует вызывающий.
 */
export function useConfirmedLogout(): { logout: () => void; dialog: ReactNode } {
  const logout = useLogout();
  const unsentEdits = useUnsentEdits();
  const [count, setCount] = useState<number | null>(null);
  const request = useCallback(() => {
    void unsentEdits().then((unsent) => {
      if (unsent > 0) setCount(unsent);
      else void logout();
    });
  }, [unsentEdits, logout]);
  const dialog = (
    <LogoutDialog
      open={count !== null}
      onOpenChange={(open) => {
        if (!open) setCount(null);
      }}
      count={count ?? 0}
      onConfirm={logout}
    />
  );
  return { logout: request, dialog };
}

/**
 * Карточка пользователя внизу меню; меню открывается вверх, «Выйти» — последним. Сеанс по ссылке
 * (030, ASG-ONB-14) — «Вы вошли как Новиков Н. Н.» и только «Выйти»: профиля и клавиш у него нет.
 */
function UserMenu({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const me = useMe();
  const { setShortcutsOpen } = useShell();
  const navigate = useNavigate();
  const { logout, dialog } = useConfirmedLogout();
  const roles = rolesText(me.roles.map((item) => item.role).filter(unique));

  if (me.linkSession) {
    return (
      <>
        <Dropdown
          label="Меню пользователя"
          side="top"
          align="start"
          trigger={
            <UserCardButton
              fullName={me.person?.name ?? me.user.fullName}
              roles=""
              signedInAs
              collapsed={collapsed}
            />
          }
        >
          <DropdownItem icon={LogOut} onSelect={logout}>
            Выйти
          </DropdownItem>
        </Dropdown>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Dropdown
        label="Меню пользователя"
        side="top"
        align="start"
        trigger={<UserCardButton fullName={me.user.fullName} roles={roles} collapsed={collapsed} />}
      >
        <DropdownItem
          icon={UserRound}
          onSelect={() => {
            onNavigate?.();
            void navigate({ to: '/profile' });
          }}
        >
          Профиль
        </DropdownItem>
        <DropdownItem icon={Keyboard} shortcut="?" onSelect={() => setShortcutsOpen(true)}>
          Список клавиш
        </DropdownItem>
        <DropdownSeparator />
        <DropdownItem icon={LogOut} onSelect={logout}>
          Выйти
        </DropdownItem>
      </Dropdown>
      {dialog}
    </>
  );
}

/**
 * Карточка пользователя — кнопка меню пользователя. У свёрнутого меню ФИО и роль спрятаны —
 * подсказка справа называет их. Принимает обработчики и ref от меню: оно же её открывает.
 */
function UserCardButton({
  fullName,
  roles,
  signedInAs = false,
  collapsed,
  ref,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  fullName: string;
  roles: string;
  /** Сеанс по ссылке (030): строкой над ФИО — «Вы вошли как». */
  signedInAs?: boolean;
  collapsed: boolean;
  ref?: Ref<HTMLButtonElement>;
}) {
  const tooltip = signedInAs
    ? `Вы вошли как ${fullName}`
    : roles
      ? `${fullName}, ${roles}`
      : fullName;
  return (
    <Tooltip content={tooltip} side="right" disabled={!collapsed}>
      <button
        ref={ref}
        type="button"
        {...rest}
        className={cx('kp-user', collapsed && 'is-collapsed')}
      >
        <Avatar fullName={fullName} square />
        <span className="kp-user-text">
          {signedInAs ? (
            <>
              <span className="kp-user-role">Вы вошли как</span>{' '}
            </>
          ) : null}
          <span className="kp-user-name">{fullName}</span>{' '}
          {roles ? <span className="kp-user-role">{roles}</span> : null}
        </span>
      </button>
    </Tooltip>
  );
}

function unique<T>(value: T, index: number, list: readonly T[]): boolean {
  return list.indexOf(value) === index;
}
